import { makeStyles } from '@material-ui/core/styles';
import { Suspense, useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { BrowserRouter, Redirect, Route, Switch } from 'react-router-dom';

import { Context as AuthContext } from '@/context/AuthContext.js';

import { useFeatureFlag } from '@/hooks/useFeatureFlag.js';

import Dashboard from '@/pages/Dashboard.jsx';
import PrismicFallback from '@/pages/PrismicFallback.jsx';
import SearchPage from '@/pages/SearchPage.jsx';

import { Auth } from '@/service/amplifyAuth.ts';

import { lazyWithRetry } from '@/utils/chunk-reload-utils.ts';
import {
    isCorrectWhiteLabelDomain,
    isDevEnvironment,
    isEternoDomain,
    isLocalEnvironment,
    isZeroDomain,
    processAPIWithHeaders,
    redirectToEternoCloud,
    redirectToZero,
} from '@/utils/helpers.js';
import { loadOrCreateUserProfile } from '@/utils/registerUtils.js';

import {
    useBookingStore,
    useConfigStore,
    useCookiesStore,
    usePersistedPrismicStore,
    usePrismicStore,
} from '@/store.js';

import CookieConstant from './Common/CookieConstant.jsx';
import CookieConstantMain from './Common/CookieConstantMain.jsx';
import DownloadDocumentModal from './DownloadDocumentModal/DownloadDocumentModal.jsx';
import EternoSpinner from './EternoLogoSpinner/eterno-spinner.tsx';
import Footer from './Footer.jsx';
import Header from './Header.jsx';
import UploadDocumentModal from './UploadDocumentModal/UploadDocumentModal.jsx';

const SmartFormFlow = lazyWithRetry(() => import('@/components/SmartFormFlow.jsx'));
const Account = lazyWithRetry(() => import('@/pages/Account.jsx'));
const AppointmentConfirm = lazyWithRetry(() => import('@/pages/ConfirmAppointment.jsx'));
const AllFormsCheckIn = lazyWithRetry(() => import('@/pages/ConsentForms/AllFormsCheckIn.jsx'));
const AllFormsCode = lazyWithRetry(() => import('@/pages/ConsentForms/AllFormsCode.jsx'));
const AllFormsLocation = lazyWithRetry(() => import('@/pages/ConsentForms/AllFormsLocation.jsx'));
const AllFormsThankYouPage = lazyWithRetry(() => import('@/pages/ConsentForms/AllFormsThankyou.jsx'));
const AllFormsWelcome = lazyWithRetry(() => import('@/pages/ConsentForms/AllFormsWelcome.jsx'));
const ConsentAllFormsThankYou = lazyWithRetry(() => import('@/pages/ConsentForms/ConsentAllForms/ThankYou.jsx'));
const CorterierTreatment = lazyWithRetry(
    () => import('@/pages/ConsentForms/CorterierTreatmentContract/CorterierTreatment.jsx')
);
const ThankYouPage = lazyWithRetry(
    () => import('@/pages/ConsentForms/CorterierTreatmentContract/Pages/ThankYouPage.jsx')
);
const CorterierTreatmentWelcome = lazyWithRetry(
    () => import('@/pages/ConsentForms/CorterierTreatmentContract/Pages/Welcome.jsx')
);
const HeraklesForms = lazyWithRetry(() => import('@/pages/ConsentForms/HeraklesAllForms/HeraklesForms.jsx'));
const HeraklesThankYouPage = lazyWithRetry(
    () => import('@/pages/ConsentForms/HeraklesAllForms/Pages/ThankYouPage.jsx')
);
const ConsentPrivacyWelcome = lazyWithRetry(() => import('@/pages/ConsentForms/HeraklesAllForms/Pages/Welcome.jsx'));
const HeraklesCommunicationForms = lazyWithRetry(
    () => import('@/pages/ConsentForms/HeraklesCommunication/HeraklesCommunicationForms.jsx')
);
const ConsentCommunicationThankYouPage = lazyWithRetry(
    () => import('@/pages/ConsentForms/HeraklesCommunication/Pages/ThankYouPage.jsx')
);
const ConsentCommunicationWelcome = lazyWithRetry(
    () => import('@/pages/ConsentForms/HeraklesCommunication/Pages/Welcome.jsx')
);
const HeraklesCourseForms = lazyWithRetry(() => import('@/pages/ConsentForms/HeraklesCourse/HeraklesCourseForms.jsx'));
const ConsentCourseThankYouPage = lazyWithRetry(
    () => import('@/pages/ConsentForms/HeraklesCourse/Pages/ThankYouPage.jsx')
);
const ConsentCourseWelcome = lazyWithRetry(() => import('@/pages/ConsentForms/HeraklesCourse/Pages/Welcome.jsx'));
const HeraklesTherapyForms = lazyWithRetry(
    () => import('@/pages/ConsentForms/HeraklesTherapy/HeraklesTherapyForms.jsx')
);
const ConsentAdmissionThankYouPage = lazyWithRetry(
    () => import('@/pages/ConsentForms/HeraklesTherapy/Pages/ThankYouPage.jsx')
);
const ConsentAdmissionWelcome = lazyWithRetry(() => import('@/pages/ConsentForms/HeraklesTherapy/Pages/Welcome.jsx'));
const ConsentTreatment = lazyWithRetry(() => import('@/pages/ConsentForms/TreatmentContract/ConsentTreatment.jsx'));
const ConsentTreatmentThankYouPage = lazyWithRetry(
    () => import('@/pages/ConsentForms/TreatmentContract/Pages/ThankYouPage.jsx')
);
const ConsentTreatmentWelcome = lazyWithRetry(() => import('@/pages/ConsentForms/TreatmentContract/Pages/Welcome.jsx'));
const ConsentHistory = lazyWithRetry(() => import('@/pages/ConsentManagement/History.jsx'));
const ConsentOverview = lazyWithRetry(() => import('@/pages/ConsentManagement/Overview.jsx'));
const CustomAnamnesis = lazyWithRetry(() => import('@/pages/CustomAnamnesis.jsx'));
const DoctorsDetails = lazyWithRetry(() => import('@/pages/DoctorsDetails.jsx'));
const FeedbackForm = lazyWithRetry(() => import('@/pages/Feedback/FeedbackForm.jsx'));
const FeedbackThankYou = lazyWithRetry(() => import('@/pages/Feedback/ThankYou.jsx'));
const Forms = lazyWithRetry(() => import('@/pages/Forms/Forms.jsx'));
const FormsStatic = lazyWithRetry(() => import('@/pages/Forms/FormsStatic.jsx'));
const GuestUnsubscribe = lazyWithRetry(() => import('@/pages/GuestUnsubscribe.jsx'));
const HealthDataPage = lazyWithRetry(() => import('@/pages/HealthProfileOverview/HealthDataPage.jsx'));
const HealthProfileOverview = lazyWithRetry(() => import('@/pages/HealthProfileOverview/HealthProfileOverview.jsx'));
const LegacyUploadedByMe = lazyWithRetry(() => import('@/pages/HealthProfileOverview/LegacyUploadedByMe.jsx'));
const SharedDocuments = lazyWithRetry(
    () => import('@/pages/HealthProfileOverview/SharedDocuments/SharedDocuments.jsx')
);
const Login = lazyWithRetry(() => import('@/pages/Login.jsx'));
const MyAppointments = lazyWithRetry(() => import('@/pages/MyAppointments.jsx'));
const MyDoctors = lazyWithRetry(() => import('@/pages/MyDoctors.jsx'));
const NotFound = lazyWithRetry(() => import('@/pages/NotFound.jsx'));
const QuestionPlayground = lazyWithRetry(() => import('@/pages/QuestionPlayground.jsx'));
const RegisterModal = lazyWithRetry(() => import('@/pages/Registration/RegisterModal.jsx'));
const ResetPassword = lazyWithRetry(() => import('@/pages/ResetPassword.jsx'));
const StaticPage = lazyWithRetry(() => import('@/pages/StaticPage/StaticPage.jsx'));
const UnSubscribe = lazyWithRetry(() => import('@/pages/UnSubscribe.jsx'));
const FormsInvitationPage = lazyWithRetry(() => import('@/pages/FormsInvitation/FormsInvitationPage.tsx'));
const SpinnerPlayground = lazyWithRetry(() => import('@/pages/SpinnerPlayground.jsx'));

const useStyles = makeStyles(() => ({
    spinnerContainer: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100vh',
    },
}));

const StaticPages = {
    PRIVACY: 'Privacy Policy',
    TERMS: 'Terms & Conditions',
    IMPRINT: 'Imprint',
};

const Router = () => {
    const {
        state: { user },
        setuser,
        setusersession,
        setuseraccounts,
    } = useContext(AuthContext);

    const classes = useStyles();
    const [loading, setLoading] = useState(true);
    const [customerKey, setCustomerKey] = useState(null);
    const customers = usePrismicStore((state) => state.customers);
    const showBookingModal = useBookingStore((state) => state.showBookingModal);
    const showPrismicFallback = useConfigStore((state) => state.showPrismicFallback);
    const currentCustomer = useConfigStore((state) => state.currentCustomer);
    const cookieConsentData = usePersistedPrismicStore((state) => state.cookieConsentData);

    const isDocumentExchangeFeatureFlagActivated = useFeatureFlag('document_exchange');

    const refreshTokenAtExp = (exp) => {
        const expVal = exp * 1000;
        console.debug('RefreshToken: At ', expVal);
        const now = Date.now();
        console.debug('RefreshToken: Now ', now);
        const refreshIn = expVal - now;
        console.debug('RefreshToken: In ', refreshIn);
        setTimeout(() => {
            getAndSetUserSession().catch((err) => console.debug(err));
        }, refreshIn);
    };

    const getCurrentUser = async () => {
        try {
            const authUser = await Auth.currentAuthenticatedUser();

            const { username } = authUser;
            const token = authUser.signInUserSession.idToken.jwtToken;

            const response = await loadOrCreateUserProfile(authUser);

            const userAccounts = await processAPIWithHeaders(`secure/user/accounts/${username}`, 'GET', {
                headers: {
                    Authorization: token,
                },
            });

            setuser(response);
            setuseraccounts(userAccounts);
            setusersession(authUser.signInUserSession);
            setLoading(false);
            refreshTokenAtExp(authUser.signInUserSession.idToken.payload.exp);
        } catch (err) {
            console.debug(err);
            setLoading(false);
        }
    };

    useEffect(() => {
        getCurrentUser().catch((err) => console.debug(err));
    }, []);

    const getAndSetUserSession = async () => {
        console.debug('RefreshToken: Get and set user ');
        try {
            const authUser = await Auth.currentAuthenticatedUser();
            setusersession(authUser.signInUserSession);
            refreshTokenAtExp(authUser.signInUserSession.idToken.payload.exp);
        } catch (err) {
            console.debug(err);
        }
    };

    // work for cookie

    const { i18n } = useTranslation();

    const [largeCookie, setLargeCookie] = useState(false);
    const [cookieOpen, setCookieOpen] = useState(false);

    useEffect(() => {
        const cookieAccepted = useCookiesStore.getState().accepted;

        if (cookieAccepted === null) {
            document.fonts.ready.then(() => setCookieOpen(true)).catch((err) => console.debug(err));
        }
    }, []);

    const cookieConsentContent = cookieConsentData[i18n.language];

    const setEternoCustomer = () => {
        useConfigStore.setState({ currentCustomer: customers[import.meta.env.VITE_ETERNO_CUSTOMER_KEY] });
        setCustomerKey(import.meta.env.VITE_ETERNO_CUSTOMER_KEY);
    };

    const setCustomer = (key) => {
        useConfigStore.setState({ currentCustomer: customers[key] });
        setCustomerKey(key);
    };

    useEffect(() => {
        if (!customers) return;

        const match = window.location.pathname.match(/\/customer\/([^/]+)/);

        if (isLocalEnvironment()) {
            if (!match) {
                setEternoCustomer();
                return;
            }

            if (match && customers[match[1]] && !customers[match[1]].is_eterno_customer) {
                setCustomer(match[1]);
                return;
            }
            redirectToEternoCloud();
        }

        if (isEternoDomain()) {
            setEternoCustomer();
            return;
        }

        if (
            match &&
            customers[match[1]] &&
            !customers[match[1]].is_eterno_customer &&
            isCorrectWhiteLabelDomain(customers[match[1]].domain)
        ) {
            setCustomer(match[1]);
            return;
        }

        if (isZeroDomain()) {
            redirectToZero();
            return;
        }
        redirectToEternoCloud();
    }, [customers]);

    const acceptCookies = (essentialCookies = false, analyticsCookies = false, functionalCookies = false) => {
        useCookiesStore.setState({
            accepted: true,
            essentialCookies: essentialCookies,
            analyticsCookies: analyticsCookies,
            functionalCookies: functionalCookies,
        });
        setCookieOpen(false);
        setLargeCookie(false);
    };

    const rejectCookies = () => {
        useCookiesStore.setState({
            accepted: false,
            essentialCookies: false,
            analyticsCookies: false,
            functionalCookies: false,
        });
        setCookieOpen(false);
        setLargeCookie(false);
    };

    const PrivateRoute = ({ children, ...rest }) => {
        const renderContent = ({ location }) =>
            user ? children : <Redirect to={`/login?redirectTo=${location.pathname}${location.search}`} />;

        return <Route {...rest} render={renderContent} />;
    };

    const notIncludingArr = [
        {
            path: '/question-playground',
            Component: QuestionPlayground,
        },
        {
            path: '/spinner-playground',
            Component: SpinnerPlayground,
        },
    ];

    if (showPrismicFallback) return <PrismicFallback />;

    if (loading || !customers || !customerKey || !currentCustomer) {
        return (
            <div className={classes.spinnerContainer}>
                <EternoSpinner />
            </div>
        );
    }

    return (
        <BrowserRouter basename={currentCustomer?.is_eterno_customer ? '' : `/customer/${customerKey}`}>
            <Header />
            <Suspense
                fallback={
                    <div className={classes.spinnerContainer}>
                        <EternoSpinner />
                    </div>
                }
            >
                <Switch>
                    <Route exact path="/">
                        {!user || (user && showBookingModal) ? <SearchPage /> : <Dashboard />}
                    </Route>
                    <Redirect exact from="/dashboard" to="/" />
                    <Redirect exact from="/find-appointment/:id" to="/?selected_professional=:id" />
                    <Redirect exact from="/find-appointment" to="/" />
                    <Redirect from="/book-appointment" to="/" />
                    <Redirect exact from="/professional-details/:id" to="/professional/:id" />
                    <Route exact path="/login">
                        {user ? <Redirect to="/" /> : <Login />}
                    </Route>
                    <Route exact path="/privacy">
                        <StaticPage page={StaticPages.PRIVACY} />
                    </Route>
                    <Route exact path="/terms">
                        <StaticPage page={StaticPages.TERMS} />
                    </Route>
                    <Route exact path="/imprint">
                        <StaticPage page={StaticPages.IMPRINT} />
                    </Route>
                    <Route exact path="/reset-password">
                        <ResetPassword />
                    </Route>
                    <Route exact path="/register">
                        <RegisterModal />
                    </Route>
                    <Route exact path="/professional/:id">
                        <DoctorsDetails />
                    </Route>
                    <Route exact path="/forms/static">
                        <FormsStatic />
                    </Route>
                    <Route exact path="/forms/:code">
                        <Forms />
                    </Route>
                    <Route exact path="/invitation/:token">
                        <FormsInvitationPage />
                    </Route>
                    <Route exact path="/smart-form-flow">
                        <SmartFormFlow />
                    </Route>
                    <PrivateRoute exact path="/shared-documents">
                        {isDocumentExchangeFeatureFlagActivated ? (
                            <SharedDocuments />
                        ) : (
                            <Redirect to="/uploaded-by-me" />
                        )}
                    </PrivateRoute>
                    <PrivateRoute exact path="/shared-documents/others">
                        {isDocumentExchangeFeatureFlagActivated ? (
                            <SharedDocuments tab="others" />
                        ) : (
                            <Redirect to="/uploaded-by-me" />
                        )}
                    </PrivateRoute>
                    <PrivateRoute exact path="/uploaded-by-me">
                        {isDocumentExchangeFeatureFlagActivated ? (
                            <Redirect to="/shared-documents" />
                        ) : (
                            <LegacyUploadedByMe />
                        )}
                    </PrivateRoute>
                    <Route exact path="/all-forms-welcome">
                        <AllFormsWelcome />
                    </Route>
                    <Route exact path="/all-forms-thankyou">
                        <AllFormsThankYouPage />
                    </Route>
                    <Route exact path="/all-forms-checkin">
                        <AllFormsCheckIn />
                    </Route>
                    <Route exact path="/consent-privacy">
                        <HeraklesForms />
                    </Route>
                    <Route exact path="/consent-privacy-welcome">
                        <ConsentPrivacyWelcome />
                    </Route>
                    <Route exact path="/consent-privacy-thankyou">
                        <HeraklesThankYouPage />
                    </Route>
                    <Route exact path="/consent-admission">
                        <HeraklesTherapyForms />
                    </Route>
                    <Route exact path="/consent-admission-welcome">
                        <ConsentAdmissionWelcome />
                    </Route>
                    <Route exact path="/consent-admission-thankyou">
                        <ConsentAdmissionThankYouPage />
                    </Route>
                    <Route exact path="/consent-communication">
                        <HeraklesCommunicationForms />
                    </Route>
                    <Route exact path="/consent-communication-welcome">
                        <ConsentCommunicationWelcome />
                    </Route>
                    <Route exact path="/consent-communication-thankyou">
                        <ConsentCommunicationThankYouPage />
                    </Route>
                    <Route exact path="/herakles-course">
                        <HeraklesCourseForms />
                    </Route>
                    <Route exact path="/herakles-course-welcome">
                        <ConsentCourseWelcome />
                    </Route>
                    <Route exact path="/herakles-course-thankyou">
                        <ConsentCourseThankYouPage />
                    </Route>
                    <Route exact path="/consent-treatment-welcome">
                        <ConsentTreatmentWelcome />
                    </Route>
                    <Route exact path="/consent-treatment-thankyou">
                        <ConsentTreatmentThankYouPage />
                    </Route>
                    <Route exact path="/consent-treatment">
                        <ConsentTreatment />
                    </Route>
                    <Route exact path="/consent-treatment-extended">
                        <CorterierTreatment />
                    </Route>
                    <Route exact path="/extended-treatment-welcome">
                        <CorterierTreatmentWelcome />
                    </Route>
                    <Route exact path="/extended-treatment-thankyou">
                        <ThankYouPage />
                    </Route>
                    <Route exact path="/all-forms-code">
                        <AllFormsCode />
                    </Route>
                    <Route exact path="/all-forms-location">
                        <AllFormsLocation />
                    </Route>
                    <Route exact path="/consent-all-forms-thankyou">
                        <ConsentAllFormsThankYou />
                    </Route>
                    <Route exact path="/guest/unsubscribe">
                        <GuestUnsubscribe />
                    </Route>
                    <Route exact path="/custom-anamnesis">
                        <CustomAnamnesis />
                    </Route>
                    <Route exact path="/feedback">
                        <FeedbackForm />
                    </Route>
                    <Route exact path="/feedback-thankyou">
                        <FeedbackThankYou />
                    </Route>
                    <Route exact path="/professionals">
                        <MyDoctors />
                    </Route>
                    <Redirect exact from="/practitioners" to="/professionals" />

                    {isDevEnvironment() &&
                        notIncludingArr.map(({ path, Component }) => (
                            <Route exact key={path} path={path}>
                                <Component />
                            </Route>
                        ))}

                    <PrivateRoute path="/appointments/:id">
                        <MyAppointments />
                    </PrivateRoute>
                    <PrivateRoute exact path="/appointments">
                        <MyAppointments />
                    </PrivateRoute>
                    <PrivateRoute path="/unsubscribe">
                        <UnSubscribe />
                    </PrivateRoute>
                    <PrivateRoute path="/account">
                        <Account />
                    </PrivateRoute>
                    <PrivateRoute path="/health-profile-overview">
                        <HealthProfileOverview />
                    </PrivateRoute>
                    <PrivateRoute path="/health-data">
                        <HealthDataPage />
                    </PrivateRoute>
                    <PrivateRoute path="/confirm-appointment">
                        <AppointmentConfirm />
                    </PrivateRoute>
                    <PrivateRoute path="/consent-overview">
                        <ConsentOverview />
                    </PrivateRoute>
                    <PrivateRoute path="/consent-history">
                        <ConsentHistory />
                    </PrivateRoute>
                    <Route path="*" component={NotFound} />
                </Switch>
            </Suspense>
            {isDocumentExchangeFeatureFlagActivated && <UploadDocumentModal />}
            {isDocumentExchangeFeatureFlagActivated && <DownloadDocumentModal />}
            <Footer />
            {/* cookie work start */}
            {cookieOpen && !largeCookie && cookieConsentContent && (
                <CookieConstant
                    open={cookieOpen}
                    onClose={() => setCookieOpen(false)}
                    setLargeCookie={setLargeCookie}
                    mainData={cookieConsentContent}
                    acceptCookies={acceptCookies}
                    rejectCookies={rejectCookies}
                />
            )}

            {cookieConsentContent && (
                <CookieConstantMain
                    open={largeCookie}
                    onClose={() => setLargeCookie(false)}
                    mainData={cookieConsentContent}
                    acceptCookies={acceptCookies}
                />
            )}
            {/* cookie work end */}
        </BrowserRouter>
    );
};

export default Router;
